import {
    areFieldsSet,
    parseAspDate,
} from '@superbet-group/web.lib.utilities';
import { BonusType } from '@superbet-group/web.types';

import { logger } from '@app/logger';
import { transformConditions } from '@app/promotionHub/shared/mappers/transformConditions';
import {
    CustomContentDto,
    CustomContentSection,
    CustomContentType,
} from '@app/promotionHub/shared/types/CustomContent';
import {
    BonusKind,
    ComtradeBonus,
} from '@core/models/bonus/Bonus';
import BonusCategory from '@core/models/bonus/BonusCategory';
import { BonusStatus } from '@core/models/bonus/BonusStatus';
import { BonusSubtype } from '@core/models/bonus/BonusSubtype';
import {
    BonusTypeComtrade,
    isComtradeBonusType,
} from '@app/promotionHub/bonuses/types/BonusTypeDto';

import ProductTypeDto from '@core/mappers/bonus/dtos/ProductTypeDto';
import { BonusSubCategory } from '@core/mappers/user/dtos/BalanceDto';
import {
    getBonusDescriptionTerms,
    getBonusTermsAndConditionsUrl,
} from '@src/app/promotionHub/bonuses/utils/bonusHelpers';
import { isCallToActionTextValid } from '@src/app/promotionHub/shared/utils/helpers';
import { isValidUrl } from '@src/app/utils/domainHelpers';
import {
    ComtradeBonusDto,
    BonusStatusDto,
    BonusAwardingDto,
} from '@src/app/promotionHub/bonuses/types/comtradeDto/ComtradeBonusDto';
import { AwardingConditions } from '@core/models/bonus/AwardingConditions';
import { getConfig } from '@superbet-group/web.lib.config';
import { PromoHubActionButton } from '@src/app/promotionHub/shared/types/PromoHubActionButton';

export function transformComtradeBonus(dto: ComtradeBonusDto): ComtradeBonus {
    const { bonusType, bonusSubtype } = getBonusTypeAndSubtype(dto);
    const nonWageringAmount = dto.Status === BonusStatusDto.Active ? Math.max((dto.BonusAwarded || 0) - dto.BonusAmount, 0) : 0;
    const bonusDescription = getBonusDescriptionTerms(dto.BonusFriendlyDescription || dto.BonusDescription);

    return {
        kind: BonusKind.Comtrade,
        id: dto.PlayerBonusId,
        name: dto.BonusFriendlyName || dto.BonusName,
        redeemedAmount: dto.RedeemedAmount || 0,
        amountLeftToWager: getAmountLeftToWager(dto, bonusSubtype),
        wageredAmount: getBonusWageredAmount(dto, bonusSubtype, nonWageringAmount),
        expirationDate: parseAspDate(dto.ExpirationDate),
        type: bonusType,
        status: getBonusStatus(dto.Status),
        category: getBonusCategory(dto.Status, dto.BonusType),
        description: bonusDescription,
        termsAndConditionsUrl: getBonusTermsAndConditionsUrl(bonusDescription.termsAndConditionsUrl),
        bonusSubtype: bonusSubtype,
        bonusPotentialWin: dto.BonusAmount,
        pendingAmount: 0, // Currently not supported by iCore (too expensive)
        bonusUsedAmount: getBonusUsedAmount(dto, bonusSubtype, nonWageringAmount),
        bonusGainedAmount: getBonusGainedAmount(dto.BonusAmount, dto.BonusAwarded),
        completedDate: getCompletedDate(dto),
        activationDate: getActivationDate(dto.DeclinedDate, dto.AwardedDate),
        wageringRequirements: getWageringRequirements(dto, bonusSubtype),
        totalWageredBonus: dto.TotalWageredBonus,
        bonusActionButton: getBonusActionButton(dto),
        conditions: transformConditions(dto.CustomContentList, CustomContentSection.Bonus, dto, bonusType),
        ...areFieldsSet(dto, 'CustomContentList') && { bonusEligibilityDescription: dto.CustomContentList.find((item) => item.Type === CustomContentType.bonusEligibilityTitle)?.Content },
        ...areFieldsSet(dto, 'BonusItemsAwarded') && { bonusItemsAwarded: parseInt(dto.BonusItemsAwarded) },
        ...areFieldsSet(dto, 'CategoryId') && { subCategory: dto.CategoryId },
        ...areFieldsSet(dto, 'BonusAwarded') && { bonusAmount: dto.BonusAwarded, bonusAwarded: dto.BonusAwarded },
        ...areFieldsSet(dto, 'AcceptedDate') && { acceptanceDate: parseAspDate(dto.AcceptedDate) },
        ...areFieldsSet(dto, 'PromotionId') && { promotionId: dto.PromotionId },
        ...areFieldsSet(dto, 'ExternalBonusProductSupplier') && { provider: dto.ExternalBonusProductSupplier },
        ...areFieldsSet(dto, 'BonusAwarding') && { awardingConditions: getAwardingConditions(dto.BonusAwarding) },
        ...areFieldsSet(dto, 'Priority') && { priority: dto.Priority },
    };
}

function getBonusTypeAndSubtype(bonusDto: ComtradeBonusDto) {
    const bonusType = getBonusType(bonusDto);
    const bonusSubtype = bonusType === BonusType.Freebet
        ? BonusSubtype.NonWagering
        : BonusSubtype.Wagering;

    return { bonusType, bonusSubtype };
}

function getBonusType(bonusDto: ComtradeBonusDto): BonusType {
    if (isComtradeBonusType(bonusDto.BonusType) && typeMappings[bonusDto.BonusType][bonusDto.ProductTypeName]) {
        const bonusType = typeMappings[bonusDto.BonusType][bonusDto.ProductTypeName]!;
        if (bonusType === BonusType.Sport && bonusDto.CategoryId === BonusSubCategory.FreeBet) {
            return BonusType.Freebet;
        } else if (bonusType === BonusType.FreeSpins && bonusDto.FreeSpinType === 'Bingo') {
            return BonusType.Bingo;
        } else if (bonusType === BonusType.Casino && getConfig().countrySpecific.user.bonus.isLiveDealerBonusVisible) {
            return BonusType.LiveDealer;
        } else {
            return bonusType === BonusType.FreeSpins && bonusDto.WageringRequirement !== null ? BonusType.Casino : bonusType;
        }
    } else {
        throw new Error(`Undefined type for bonus and product type combination: ${bonusDto.BonusType}, ${bonusDto.ProductTypeName} `);
    }
}

const typeMappings: Record<BonusTypeComtrade, Partial<Record<ProductTypeDto, BonusType>>> = {
    [BonusTypeComtrade.Casino]: {
        [ProductTypeDto.Casino]: BonusType.Casino,
        [ProductTypeDto.CasinoVirtuals]: BonusType.CasinoVirtuals,
    },
    [BonusTypeComtrade.External]: {
        [ProductTypeDto.Casino]: BonusType.FreeSpins,
    },
    [BonusTypeComtrade.Sportsbook]: {
        [ProductTypeDto.Sportsbook]: BonusType.Sport,
        [ProductTypeDto.Virtuals]: BonusType.Virtuals,
    },
};

function getBonusGainedAmount(bonusAmount: number, bonusAwarded?: number | null): number {
    const ba = bonusAwarded || 0;
    return Math.max(bonusAmount - ba, 0);
}

function getBonusStatus(statusDto: BonusStatusDto): BonusStatus {
    const status = statusMappings[statusDto];
    if (!status) {
        logger.error(new Error(`Undefined status for bonus status: ${statusDto}`));
    }
    return status;
}

const statusMappings: Record<BonusStatusDto, BonusStatus> = {
    [BonusStatusDto.Declined]: BonusStatus.Declined,
    [BonusStatusDto.Active]: BonusStatus.Active,
    [BonusStatusDto.Pending]: BonusStatus.Pending,
    [BonusStatusDto.Expired]: BonusStatus.Expired,
    [BonusStatusDto.Lost]: BonusStatus.Lost,
    [BonusStatusDto.Redeemed]: BonusStatus.Redeemed,
    [BonusStatusDto.Waiting]: BonusStatus.Waiting,
    [BonusStatusDto.WaitingManual]: BonusStatus.Waiting,
    [BonusStatusDto.AwardedExternal]: BonusStatus.AwardedExternal,
};

function getBonusCategory(statusDto: BonusStatusDto, bonusType: BonusTypeComtrade): BonusCategory {
    const category = categoryMappings[statusDto];
    if (!category) {
        logger.error(new Error(`Undefined category for bonus status: ${statusDto}`));
    }
    return typeof category === 'object' ? category[bonusType] : category;
}

const categoryMappings: Record<BonusStatusDto, BonusCategory | Record<BonusTypeComtrade, BonusCategory>> = {
    [BonusStatusDto.Declined]: BonusCategory.History,
    [BonusStatusDto.Active]: BonusCategory.Active,
    [BonusStatusDto.Pending]: {
        [BonusTypeComtrade.Sportsbook]: BonusCategory.AvailableToWager,
        [BonusTypeComtrade.Casino]: BonusCategory.UnavailableToWager,
        [BonusTypeComtrade.External]: BonusCategory.UnavailableToWager,
    },
    [BonusStatusDto.Expired]: BonusCategory.History,
    [BonusStatusDto.Lost]: BonusCategory.History,
    [BonusStatusDto.Redeemed]: BonusCategory.History,
    [BonusStatusDto.Waiting]: BonusCategory.CriteriaRequired,
    [BonusStatusDto.WaitingManual]: BonusCategory.CriteriaRequired,
    [BonusStatusDto.AwardedExternal]: BonusCategory.CriteriaRequired,
};

function getActivationDate(declinedDate: string | null, awardedDate?: string | undefined): Date | undefined {
    if (declinedDate) {
        return parseAspDate(declinedDate);
    } else if (awardedDate) {
        return parseAspDate(awardedDate);
    }
    return undefined;
}

function getAmountLeftToWager(dto: ComtradeBonusDto, bonusSubtype: BonusSubtype) {
    const nonWageringAmount = dto.Status === BonusStatusDto.Pending ? dto.BonusAwarded : dto.BonusAmount;

    return bonusSubtype === BonusSubtype.Wagering
        ? dto.WageringRequirementLeft || 0
        : nonWageringAmount || 0;
}

function getBonusWageredAmount(dto: ComtradeBonusDto, bonusSubtype: BonusSubtype, nonWageringAmount: number) {
    return bonusSubtype === BonusSubtype.Wagering
        ? dto.TotalWagered
        : nonWageringAmount;
}

function getWageringRequirements(dto: ComtradeBonusDto, bonusSubtype: BonusSubtype) {
    return bonusSubtype === BonusSubtype.Wagering && dto.WageringRequirement
        ? dto.WageringRequirement
        : dto.BonusAwarded || 0;
}

function getBonusUsedAmount(dto: ComtradeBonusDto, bonusSubtype: BonusSubtype, nonWageringAmount: number) {
    return bonusSubtype === BonusSubtype.Wagering
        ? dto.TotalWageredBonus || 0
        : nonWageringAmount;
}

// Returns date that is smallest of the RedeemedDate, LostDate, DeclinedDate
function getCompletedDate(dto: ComtradeBonusDto) {
    const completedDates = [dto.RedeemedDate, dto.LostDate, dto.DeclinedDate].filter(Boolean) as string[];
    const smallestDate = Math.min(...completedDates.map((date) => parseInt(date.substr(6))));

    return new Date(smallestDate);
}

function getAwardingConditions(conditions: BonusAwardingDto): AwardingConditions[] {
    return conditions.AwardConditionFulfilment.map((condition) => {
        return {
            type: condition.TriggerType,
            amountFulfilled: condition.PlayerFulfillmentTotal,
            amountNeeded: condition.PlayerFulfillmentLeft,
            totalAmount: condition.PlayerFulfillmentTotal + condition.PlayerFulfillmentLeft,
            timeTriggerType: condition.TimePeriodType,
            isConditionMet: condition.AwardConditionMet,
        };
    });
}

function getBonusActionButton(dto: ComtradeBonusDto): PromoHubActionButton {
    return {
        text: areFieldsSet(dto, 'CustomContentList') ? getBonusActionButtonText(dto.CustomContentList) : undefined,
        link: areFieldsSet(dto, 'CustomContentList') ? getBonusActionButtonDeeplink(dto.CustomContentList) : undefined,
    };
}

function getBonusActionButtonText(customContent: CustomContentDto[]): string | undefined {
    const text = customContent.find((item) => item.Type === CustomContentType.bonusButtonCtaText)?.Content;
    return text && isCallToActionTextValid(text) ? text : undefined;
}

function getBonusActionButtonDeeplink(customContent: CustomContentDto[]): string | undefined {
    const deeplink = customContent.find((item) => item.Type === CustomContentType.bonusButtonCtaUrl)?.Content;
    return deeplink && isValidUrl(deeplink) ? deeplink : undefined;
}
